import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import type { AreaLocation } from "@/features/deployment-dashboard/types";
import { useFacilitySchematicAssets } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/hooks/use-facility-schematic-assets";
import { registerSvgImageMap } from "@/shared/charts";
import { GeoScatterChart } from "@/shared/charts";
import type { GeoScatterBoundary } from "@/shared/charts";
import { Callout, LoadingState } from "@/shared/ui";
import {
  getThroughputLineOptionKey,
  type ThroughputLineOption,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";
import {
  createThroughputDirectionalLine,
  getThroughputLineMapName,
  transformThroughputLineGeometry,
} from "@/features/deployment-dashboard/widgets/panels/throughput-line-map/throughput-line-map.utils";

const THROUGHPUT_LINE_MAP_ZOOM_LIMIT = { min: 1, max: 4 };
const HOVERED_LINE_WIDTH = 4;
const SAME_AREA_LINE_OPACITY = 0.25;
const MAP_HEIGHT = 360;
const MAP_ERROR_MESSAGE = "Failed to load this floor plan. Try reloading the page.";

export interface ThroughputAreaLineMapProps {
  deploymentId: string;
  area: Pick<AreaLocation, "id" | "name">;
  lines: ThroughputLineOption[];
  selectedLineKeys: ReadonlySet<string>;
  hoveredLineKey: string | null;
  onFloorplanSizeChange?: (areaId: string, size: { width: number; height: number }) => void;
}

/** Renders all configured throughput lines over one area's floor plan, emphasizing selected lines. */
export function ThroughputAreaLineMap({
  deploymentId,
  area,
  lines,
  selectedLineKeys,
  hoveredLineKey,
  onFloorplanSizeChange,
}: ThroughputAreaLineMapProps) {
  const theme = useTheme();
  const [registeredMapSource, setRegisteredMapSource] = useState<string | null>(null);
  const [focusRequestId, setFocusRequestId] = useState(0);
  const { status, floorplanImageUrl, floorplanImageSize, transform } = useFacilitySchematicAssets(
    deploymentId,
    area.id
  );
  const mapName = getThroughputLineMapName(deploymentId, area.id);
  const mapSource =
    floorplanImageUrl && floorplanImageSize
      ? JSON.stringify([floorplanImageUrl, floorplanImageSize.width, floorplanImageSize.height])
      : null;

  useEffect(() => {
    if (!floorplanImageUrl || !floorplanImageSize || mapSource == null) return;

    registerSvgImageMap({
      mapName,
      imageUrl: floorplanImageUrl,
      imageWidth: floorplanImageSize.width,
      imageHeight: floorplanImageSize.height,
    });
    setRegisteredMapSource(mapSource);
  }, [floorplanImageUrl, floorplanImageSize, mapName, mapSource]);

  useEffect(() => {
    if (!floorplanImageSize) return;
    onFloorplanSizeChange?.(area.id, floorplanImageSize);
  }, [area.id, floorplanImageSize, onFloorplanSizeChange]);

  const hoveredLine = useMemo(
    () => lines.find((line) => getThroughputLineOptionKey(line) === hoveredLineKey) ?? null,
    [hoveredLineKey, lines]
  );
  const isOtherAreaHovered = hoveredLineKey != null && hoveredLine == null;
  const focusedLineId = hoveredLine?.line_id ?? null;

  useEffect(() => {
    setFocusRequestId((requestId) => requestId + 1);
  }, [focusedLineId]);
  const focusBoundaries = useMemo<GeoScatterBoundary[]>(() => {
    if (!transform || !floorplanImageSize) return [];

    if (hoveredLine) {
      const geometry = transformThroughputLineGeometry(hoveredLine.line_geometry, transform);
      return [
        {
          id: `${area.id}:throughput-focus:${hoveredLine.line_id}`,
          points: [geometry.start, geometry.end],
          closed: false,
        },
      ];
    }

    const { width, height } = floorplanImageSize;
    return [
      {
        id: `${area.id}:throughput-floorplan-focus`,
        points: [
          [0, 0],
          [width, 0],
          [width, height],
          [0, height],
        ],
      },
    ];
  }, [area.id, floorplanImageSize, hoveredLine, transform]);

  const directionalLines = useMemo(() => {
    if (!transform) return [];

    const isAreaHovered = hoveredLine != null;
    const activeColors = {
      line: theme.palette.common.black,
      positive: theme.palette.success.main,
      negative: theme.palette.error.main,
    };
    const inactiveColors = {
      line: theme.palette.grey[500],
      positive: theme.palette.grey[300],
      negative: theme.palette.grey[700],
    };

    return lines.map((line) => {
      const isSelected = selectedLineKeys.has(getThroughputLineOptionKey(line));
      const isHovered = hoveredLine?.line_id === line.line_id;
      const colors = isSelected ? activeColors : inactiveColors;
      const emphasis = isHovered
        ? {
            width: HOVERED_LINE_WIDTH,
            opacity: 1,
            labelColor: theme.palette.primary.contrastText,
            labelBackgroundColor: theme.palette.primary.main,
            labelBorderColor: theme.palette.primary.dark,
          }
        : isOtherAreaHovered
          ? { opacity: 0.55 }
          : isSelected && isAreaHovered
            ? { opacity: SAME_AREA_LINE_OPACITY }
            : !isSelected
              ? { opacity: 0.72 }
              : undefined;

      return createThroughputDirectionalLine(line, transform, colors, emphasis);
    });
  }, [hoveredLine, isOtherAreaHovered, lines, selectedLineKeys, theme, transform]);

  if (status === "map-error" || status === "image-error") {
    return (
      <Box sx={{ minHeight: MAP_HEIGHT, display: "flex", alignItems: "center" }}>
        <Callout severity="error" title="Floor plan unavailable">
          {MAP_ERROR_MESSAGE}
        </Callout>
      </Box>
    );
  }

  if (status === "no-floorplan") {
    return (
      <Box sx={{ minHeight: MAP_HEIGHT, display: "flex", alignItems: "center" }}>
        <Callout severity="info" title="No floor plan configured">
          No floor plan configured for {area.name}.
        </Callout>
      </Box>
    );
  }

  if (
    status === "loading-map" ||
    status === "loading-image" ||
    floorplanImageUrl == null ||
    floorplanImageSize == null ||
    registeredMapSource !== mapSource
  ) {
    return <LoadingState message={`Loading ${area.name} floor plan...`} />;
  }

  return (
    <Box
      component="article"
      aria-label={`${area.name} throughput line map`}
      sx={{
        position: "relative",
        width: "100%",
        height: MAP_HEIGHT,
        minWidth: 0,
        opacity: isOtherAreaHovered ? 0.55 : 1,
        transition: "opacity 180ms ease",
      }}
    >
      <GeoScatterChart
        key={`${deploymentId}:${area.id}:${mapSource}`}
        markers={[]}
        directionalLines={directionalLines}
        config={{
          mapName,
          ariaLabel: `${area.name} throughput line map`,
          zoomLimit: THROUGHPUT_LINE_MAP_ZOOM_LIMIT,
        }}
        focusBoundaries={focusBoundaries}
        focusRequestId={focusRequestId}
        height="100%"
      />
    </Box>
  );
}
